import { memo } from 'react';
import type { IdeaDetailPanelProps } from './IdeaDetailPanel.types';
import type { BenchmarkReferenceWithImage } from '../../../model/ideation-widget.types';
import { ideaToAsset } from '../../../model/ideation-widget.types';
import { IdeaDetailHeader } from '../IdeaDetailHeader';
import { WhyItWorksCard } from '../WhyItWorksCard';
import { ExecutionDetailCard } from '../ExecutionDetailCard';
import { ScoreTabCard } from '../ScoreTabCard';

import { getWidgetStores } from '@/entities/widget';
import { MotionBox } from '@/shared/ui/motion';
import { useTranslation } from 'react-i18next';

/** Section heading rule + title, repeated by every block below the header. */
const SectionHeading = ({ children, className }: { children: string; className?: string }) => (
  <div
    className={`flex items-center justify-between border-t border-border-light pt-4 ${
      className ?? 'pb-6'
    }`}
  >
    <p className="text-heading-md font-medium text-foreground">{children}</p>
  </div>
);

export const IdeaDetailPanel = memo(function IdeaDetailPanel({
  idea,
  summary,
  benchmarkReferences,
  exporting = false,
}: IdeaDetailPanelProps) {
  const { t } = useTranslation('contentTest');

  const mainImage = ideaToAsset(idea);
  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  // Extract context info from summary
  const contextInfo = summary
    ? {
        creativeMission: summary.creativeMission,
        keyElements: summary.keyStrategicElements,
        constraints: summary.constraints,
      }
    : null;

  // Live UI caps these for the side panel; the export renders every section in
  // full (see pdf-export docs, and the overview export which stacks all refs).
  const displayedKeyElements: Array<{ title: string; content: string }> = exporting
    ? contextInfo?.keyElements || []
    : contextInfo?.keyElements.slice(0, 3) || [];

  const displayedBenchmarks: BenchmarkReferenceWithImage[] = benchmarkReferences
    ? exporting
      ? benchmarkReferences
      : benchmarkReferences.slice(0, 4)
    : [];

  // Live UI clamps the benchmark description to keep cards compact; the export
  // shows the full text.
  const benchmarkDescriptionClamp = exporting ? '' : 'line-clamp-2';

  return (
    <div className="flex w-full flex-col">
      {/* Header Section - Light blue background with title and image */}
      <IdeaDetailHeader title={idea.title} description={idea.description} mainImage={mainImage} />

      {/* Why It Works Section */}
      {idea.whyThisWorks && (
        <div className="flex flex-col items-center px-15 pt-20">
          <div className="w-full px-6">
            <WhyItWorksCard content={idea.whyThisWorks} />
          </div>
        </div>
      )}

      {/* Context Section - Show strategic elements from summary */}
      {displayedKeyElements.length > 0 && (
        <div className="flex flex-col px-15 py-10">
          <SectionHeading>{t('ideationWidget.ideaDetailPanel.strategicContext')}</SectionHeading>
          <div className={`flex gap-6 px-6 ${isFullscreen ? 'flex-row' : 'flex-col'}`}>
            {displayedKeyElements.map((element, index) => (
              <div key={index} className="flex flex-1 flex-col gap-2 rounded-xl bg-surface p-6">
                <p className="text-body-lg font-medium text-primary">{element.title}</p>
                <p className="text-body-lg text-foreground">{element.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Benchmark References Section */}
      {displayedBenchmarks.length > 0 && (
        <div className="flex flex-col px-15 py-10">
          <SectionHeading>{t('ideationWidget.ideaDetailPanel.relatedBenchmarks')}</SectionHeading>
          <div className="flex flex-wrap gap-4 px-6">
            {displayedBenchmarks.map((reference, index) => (
              <div
                key={index}
                className={`flex min-w-50 flex-col gap-2 rounded-lg border border-border-light p-4 ${
                  isFullscreen ? 'basis-[calc(25%-16px)]' : 'basis-[calc(50%-8px)]'
                } grow`}
              >
                <p className="text-body-lg font-medium text-foreground">{reference.brand}</p>
                <p className={`text-body-lg text-muted-foreground ${benchmarkDescriptionClamp}`}>
                  {reference.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Executions Section */}
      {idea.executions.length > 0 && (
        <div className="flex flex-col gap-20 px-15 py-20">
          {idea.executions.map((execution, index) => (
            <ExecutionDetailCard key={index} execution={execution} index={index} />
          ))}
        </div>
      )}

      {/* Score Section */}
      {(idea.audienceScore.length > 0 || idea.creativeScore.length > 0) && (
        <div className="flex flex-col px-15 pb-20">
          <SectionHeading className="pb-10">
            {t('ideationWidget.ideaDetailPanel.score')}
          </SectionHeading>

          {/* Score Cards Container */}
          <MotionBox
            layout={!exporting}
            className={`flex gap-4 px-6 ${isFullscreen ? 'flex-row' : 'flex-col'}`}
          >
            {idea.audienceScore.length > 0 && (
              <ScoreTabCard type="audience" scores={idea.audienceScore} isExporting={exporting} />
            )}
            {idea.creativeScore.length > 0 && (
              <ScoreTabCard type="creative" scores={idea.creativeScore} isExporting={exporting} />
            )}
          </MotionBox>
        </div>
      )}
    </div>
  );
});
